import type { ApiErrorBody, ValidationError } from './types';

/** Any non-2xx answer from the API. `message` is always safe to show to the user. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: ApiErrorBody['detail'] | null;

  constructor(status: number, detail: ApiErrorBody['detail'] | null) {
    super(errorMessage(status, detail));
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  /** `{ field: message }` from a 422 list, keyed by the field name in `loc`. */
  get fieldErrors(): Record<string, string> {
    return fieldErrors(this.detail);
  }
}

const SERVER_PROBLEM = 'Our server ran into a problem with that request. Please try again in a minute. If it keeps happening, contact support.';
const SERVICE_BUSY = 'The service is busy or restarting. Please try again in a minute.';

const FALLBACK: Record<number, string> = {
  400: "That request wasn't accepted. Please check what you entered and try again.",
  401: 'Your session has ended. Please sign in again.',
  402: "You don't have enough coins for this.",
  403: 'You are not allowed to do that.',
  404: "We couldn't find that. It may have been removed.",
  408: 'That took too long. Please try again.',
  409: 'That can\'t be done right now because something changed. Refresh the page and try again.',
  413: 'The file is too large.',
  415: 'That file type is not supported.',
  422: "Some of the details aren't valid. Please check the form and try again.",
  429: 'Too many attempts. Please wait a few minutes and try again.',
  500: SERVER_PROBLEM,
  502: SERVICE_BUSY,
  503: SERVICE_BUSY,
  504: 'That took too long. Please try again.',
};

/** Technical API details, reworded for customers. */
const DETAIL_REWRITES: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^applicant\.full_name is required$/i, () => "Enter the applicant's full name."],
  [/^Only failed (or cancelled )?checks can be retried/i, () => 'Only a check that failed or was stopped can be run again.'],
  [/^Only queued or running checks can be cancelled/i, () => 'This check has already finished, so it can no longer be stopped.'],
  [/^Not enough coins: a check costs (\d+)$/i, (m) => `You don't have enough coins. A check costs ${m[1]} coin${m[1] === '1' ? '' : 's'}.`],
  [/^(Database|Queue) unavailable/i, () => SERVICE_BUSY],
  [/^Admins only$/i, () => 'You are not allowed to do that.'],
];

const FIELD_LABELS: Record<string, string> = {
  username: 'Email or username',
  passport_no: 'Passport number',
  passport_expiry: 'Passport expiry date',
  nationality_code: 'Nationality',
  nationality_text: 'Nationality',
  full_name: 'Full name',
  full_name_ar: 'Full name (Arabic)',
  place_of_birth: 'Place of birth',
  place_of_birth_ar: 'Place of birth (Arabic)',
  date_of_birth: 'Date of birth',
  passport_type: 'Passport type',
  current_password: 'Current password',
  new_password: 'New password',
  transaction_ref: 'Transaction reference',
  bank_account_id: 'Bank account',
};

/** What a field accepts, for "doesn't match the pattern" errors. */
const PATTERN_HINTS: Record<string, string> = {
  phone: 'Enter a phone number of 7–15 digits, optionally starting with +, for example +251911234567.',
  email: 'Enter a valid email address, like name@example.com.',
  passport_no: 'Use only letters and numbers, with no spaces or symbols.',
  full_name: 'Use English letters (A–Z) only. Spaces, hyphens and apostrophes are fine.',
  place_of_birth: 'Use English letters (A–Z) only. Spaces, hyphens and apostrophes are fine.',
  passport_type: 'Use 1 or 2 letters, for example P.',
};

/** Strip pydantic's "Value error, " prefix so messages read naturally. */
export function cleanMsg(msg: string): string {
  return msg.replace(/^Value error,\s*/i, '');
}

/** One validation error in plain language, without the field name. */
export function friendlyValidation(e: ValidationError): string {
  const field = fieldName(e.loc) ?? '';
  const ctx = e.ctx ?? {};
  const msg = cleanMsg(e.msg);
  const plain = msg.charAt(0).toUpperCase() + msg.slice(1);
  // Limits come from ctx; without them the original message is the best we have
  const needs = (key: string) => ctx[key] === undefined;
  switch (e.type) {
    case 'missing':
      return 'This field is required.';
    case 'string_too_short':
      if (needs('min_length')) return plain;
      return field.includes('password')
        ? `Must be at least ${ctx.min_length} characters long.`
        : ctx.min_length === 1 ? 'This field is required.' : `Must be at least ${ctx.min_length} characters.`;
    case 'string_too_long':
      if (needs('max_length')) return plain;
      return `Must be at most ${ctx.max_length} characters.`;
    case 'string_pattern_mismatch':
      return PATTERN_HINTS[field] ?? "Contains characters that aren't allowed here.";
    case 'greater_than':
      if (needs('gt')) return plain;
      return `Must be more than ${ctx.gt}.`;
    case 'greater_than_equal':
      if (needs('ge')) return plain;
      return `Must be at least ${ctx.ge}.`;
    case 'less_than':
      if (needs('lt')) return plain;
      return `Must be less than ${ctx.lt}.`;
    case 'less_than_equal':
      if (needs('le')) return plain;
      return `Must be at most ${ctx.le}.`;
    case 'int_parsing':
    case 'int_type':
    case 'int_from_float':
      return 'Enter a whole number.';
    case 'float_parsing':
    case 'float_type':
    case 'decimal_parsing':
      return 'Enter a number.';
    case 'enum':
    case 'literal_error':
      return 'Choose one of the options.';
    case 'json_invalid':
      return "The request couldn't be read. Please try again.";
  }
  if (/^Unrecognised (date|month)/i.test(msg)) return 'Enter a valid date, for example 12/11/2030.';
  if (/^Invalid date .*out of range/i.test(msg)) return "That date doesn't exist. Check the day and month.";
  if (/^must contain Arabic letters$/i.test(msg)) return 'Write this in Arabic letters.';
  if (/^must be in the past$/i.test(msg)) return 'Must be a date in the past.';
  return plain;
}

export function errorMessage(status: number, detail: ApiErrorBody['detail'] | null | undefined): string {
  if (typeof detail === 'string' && detail.trim()) {
    for (const [pattern, rewrite] of DETAIL_REWRITES) {
      const m = detail.trim().match(pattern);
      if (m) return rewrite(m);
    }
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as ValidationError;
    const field = fieldName(first.loc);
    return field ? `${label(field)}: ${friendlyValidation(first)}` : friendlyValidation(first);
  }
  if (status === 0) return "Can't reach the server. Check your internet connection and try again.";
  return FALLBACK[status] ?? (status >= 500 ? SERVER_PROBLEM : FALLBACK[400]!);
}

/** `loc` is ["body", field] for bodies and ["query", field] for query params. */
function fieldName(loc: (string | number)[]): string | null {
  const name = loc.length > 1 ? loc[loc.length - 1] : null;
  return typeof name === 'string' ? name : null;
}

export function fieldErrors(detail: ApiErrorBody['detail'] | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(detail)) return out;
  for (const e of detail) {
    const name = fieldName(e.loc);
    if (name && !out[name]) out[name] = friendlyValidation(e);
  }
  return out;
}

function label(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const s = field.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function isApiError(e: unknown, status?: number): e is ApiError {
  return e instanceof ApiError && (status === undefined || e.status === status);
}

/** Programming errors (and the browser's network wording) are not for customers. */
const TECHNICAL = /^(TypeError|SyntaxError|ReferenceError|RangeError)$|Failed to fetch|NetworkError|Load failed|JSON|Unexpected token|undefined|is not a function/i;

/** A message that is safe and useful to show to the user, whatever was thrown. */
export function userMessage(err: unknown, fallback = "That didn't work. Please try again."): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    if (err.name === 'AbortError') return 'The request was cancelled.';
    if (TECHNICAL.test(err.name) || TECHNICAL.test(err.message) || !err.message.trim()) return fallback;
    return err.message;
  }
  return fallback;
}
